import {
    PermissionAllowedData,
    PermissionEvidence,
    PermissionEvidencePermissionType,
    PermissionEvidenceSourceType,
    SourceStatus
} from './domain';
import {
    PlatformAdapter,
    PlatformRawListingBase
} from './platform-adapter';

export type PlatformProviderMode = 'mock' | 'real';
export type PlatformProviderState = 'mock' | 'active' | 'api_pending';
export type PlatformPermissionBlockReason =
    | 'missing_permission_evidence'
    | 'missing_contract_ref'
    | 'sensitive_contract_ref'
    | 'missing_reviewed_at'
    | 'invalid_reviewed_at'
    | 'invalid_expires_at'
    | 'permission_expired'
    | 'display_not_allowed'
    | 'price_storage_not_allowed'
    | 'image_display_not_allowed'
    | 'allowed_data_missing';

export interface PlatformPermissionRequirements {
    sourceName?: string;
    sourceType?: PermissionEvidenceSourceType;
    permissionType?: PermissionEvidencePermissionType;
    requiredData?: PermissionAllowedData[];
    requireDisplay?: boolean;
    requirePriceStorage?: boolean;
    requireImageDisplay?: boolean;
    now?: Date | string;
}

export interface PlatformPermissionGuardInput extends PlatformPermissionRequirements {
    platformId?: string;
    platformName?: string;
    evidence?: PermissionEvidence | null;
    providerMode?: PlatformProviderMode;
}

export interface PlatformPermissionGuardResult {
    allowed: boolean;
    providerMode: PlatformProviderMode;
    providerState: PlatformProviderState;
    sourceStatus: SourceStatus;
    reasons: PlatformPermissionBlockReason[];
    missingAllowedData: PermissionAllowedData[];
    evidenceId: string | null;
    contactOrContractRef: string | null;
    reviewedAt: string | null;
    expiresAt: string | null;
    message: string;
}

export interface PlatformAdapterRegistration<
    TRawListing extends PlatformRawListingBase = PlatformRawListingBase
> {
    adapter: PlatformAdapter<TRawListing>;
    providerMode: PlatformProviderMode;
    permissionEvidence?: PermissionEvidence | null;
    permissionRequirements?: PlatformPermissionRequirements;
}

export interface PlatformAdapterActivation<
    TRawListing extends PlatformRawListingBase = PlatformRawListingBase
> {
    adapter: PlatformAdapter<TRawListing>;
    active: boolean;
    guard: PlatformPermissionGuardResult;
}

export const defaultPlatformPermissionRequirements: Required<Pick<
    PlatformPermissionRequirements,
    'requiredData' | 'requireDisplay' | 'requirePriceStorage' | 'requireImageDisplay'
>> = {
    requiredData: ['product_title', 'product_url', 'listed_price'],
    requireDisplay: true,
    requirePriceStorage: true,
    requireImageDisplay: true
};

function uniqueValues<T>(values: T[] = []): T[] {
    return Array.from(new Set(values));
}

function asDate(value: string | Date | null | undefined): Date | null {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isFinite(date.getTime()) ? date : null;
}

function rawRefLooksSensitive(value: string): boolean {
    const normalized = value.trim();
    if (!normalized) return false;
    if (/https?:\/\//i.test(normalized)) return true;
    if (/[^\s@]+@[^\s@]+\.[^\s@]+/.test(normalized)) return true;
    if (/(?:\+?\d[\d\s().-]{7,}\d)/.test(normalized)) return true;
    if (/(contract|agreement|계약서|원문|본문)\s*[:=]/i.test(normalized)) return true;
    return normalized.length > 96;
}

function blockedResult(
    input: PlatformPermissionGuardInput,
    reasons: PlatformPermissionBlockReason[],
    missingAllowedData: PermissionAllowedData[]
): PlatformPermissionGuardResult {
    const evidence = input.evidence || null;

    return {
        allowed: false,
        providerMode: input.providerMode || 'real',
        providerState: 'api_pending',
        sourceStatus: 'api_pending',
        reasons,
        missingAllowedData,
        evidenceId: evidence?.id || null,
        contactOrContractRef: evidence?.contactOrContractRef || null,
        reviewedAt: evidence?.reviewedAt || null,
        expiresAt: evidence?.expiresAt || null,
        message: 'PermissionEvidence 확인 전에는 실제 플랫폼 provider를 활성화하지 않습니다.'
    };
}

export function evaluatePermissionEvidence(input: PlatformPermissionGuardInput): PlatformPermissionGuardResult {
    const providerMode = input.providerMode || 'real';

    if (providerMode === 'mock') {
        return {
            allowed: true,
            providerMode,
            providerState: 'mock',
            sourceStatus: 'mock',
            reasons: [],
            missingAllowedData: [],
            evidenceId: input.evidence?.id || null,
            contactOrContractRef: input.evidence?.contactOrContractRef || null,
            reviewedAt: input.evidence?.reviewedAt || null,
            expiresAt: input.evidence?.expiresAt || null,
            message: 'Mock provider는 외부 수집을 수행하지 않으므로 mock 상태로만 활성화됩니다.'
        };
    }

    const evidence = input.evidence || null;
    if (!evidence) {
        return blockedResult(input, ['missing_permission_evidence'], uniqueValues(input.requiredData));
    }

    const reasons: PlatformPermissionBlockReason[] = [];
    const requiredData = uniqueValues(input.requiredData || defaultPlatformPermissionRequirements.requiredData);
    const evidenceAllowedData = new Set(evidence.allowedData || []);
    const missingAllowedData = requiredData.filter((data) => !evidenceAllowedData.has(data));
    const now = asDate(input.now || new Date()) || new Date();
    const reviewedAt = asDate(evidence.reviewedAt);
    const expiresAt = evidence.expiresAt ? asDate(evidence.expiresAt) : null;

    if (!evidence.contactOrContractRef || !evidence.contactOrContractRef.trim()) {
        reasons.push('missing_contract_ref');
    } else if (rawRefLooksSensitive(evidence.contactOrContractRef)) {
        reasons.push('sensitive_contract_ref');
    }

    if (!evidence.reviewedAt) reasons.push('missing_reviewed_at');
    else if (!reviewedAt) reasons.push('invalid_reviewed_at');

    if (evidence.expiresAt && !expiresAt) reasons.push('invalid_expires_at');
    if (expiresAt && expiresAt.getTime() <= now.getTime()) reasons.push('permission_expired');

    if ((input.requireDisplay ?? defaultPlatformPermissionRequirements.requireDisplay) && !evidence.displayAllowed) {
        reasons.push('display_not_allowed');
    }
    if ((input.requirePriceStorage ?? defaultPlatformPermissionRequirements.requirePriceStorage) && !evidence.priceStorageAllowed) {
        reasons.push('price_storage_not_allowed');
    }
    if ((input.requireImageDisplay ?? defaultPlatformPermissionRequirements.requireImageDisplay) && !evidence.imageDisplayAllowed) {
        reasons.push('image_display_not_allowed');
    }
    if (missingAllowedData.length > 0) reasons.push('allowed_data_missing');

    if (reasons.length > 0) return blockedResult(input, uniqueValues(reasons), missingAllowedData);

    return {
        allowed: true,
        providerMode,
        providerState: 'active',
        sourceStatus: 'image_match_ready',
        reasons: [],
        missingAllowedData: [],
        evidenceId: evidence.id,
        contactOrContractRef: evidence.contactOrContractRef,
        reviewedAt: evidence.reviewedAt,
        expiresAt: evidence.expiresAt,
        message: 'PermissionEvidence가 확인되어 실제 플랫폼 provider 활성화가 가능합니다.'
    };
}

export function guardPlatformAdapterActivation<
    TRawListing extends PlatformRawListingBase = PlatformRawListingBase
>(
    adapter: PlatformAdapter<TRawListing>,
    input: Omit<PlatformPermissionGuardInput, 'platformId' | 'platformName'> = {}
): PlatformAdapterActivation<TRawListing> {
    const guard = evaluatePermissionEvidence({
        ...input,
        platformId: adapter.platform_id,
        platformName: adapter.platform_name,
        sourceName: input.sourceName || adapter.platform_name
    });

    return {
        adapter,
        active: guard.allowed,
        guard
    };
}

export class PermissionGuardedPlatformAdapterRegistry<
    TRawListing extends PlatformRawListingBase = PlatformRawListingBase
> {
    private readonly registrations: PlatformAdapterRegistration<TRawListing>[];

    constructor(registrations: PlatformAdapterRegistration<TRawListing>[]) {
        this.registrations = registrations.slice();
    }

    getActivations(): PlatformAdapterActivation<TRawListing>[] {
        return this.registrations.map((registration) => guardPlatformAdapterActivation(
            registration.adapter,
            {
                ...registration.permissionRequirements,
                evidence: registration.permissionEvidence,
                providerMode: registration.providerMode
            }
        ));
    }

    getActiveAdapters(): PlatformAdapter<TRawListing>[] {
        return this.getActivations()
            .filter((activation) => activation.active)
            .map((activation) => activation.adapter);
    }

    getActivation(platformId: string): PlatformAdapterActivation<TRawListing> | null {
        return this.getActivations().find((activation) => activation.adapter.platform_id === platformId) || null;
    }
}
